import { defineStore } from 'pinia';
import { EventLogger } from '@utils/event-metrics';
import { EVENTS_DICTIONARY } from '@assets/constants/events-dictionary';
import { WalletPageName } from '@assets/constants/route-names';
import { notEmpty } from '@utils/validations/not-empty';
import { useInterface } from '@stores/app';
import { CompanyAPI } from '@api/buser-http-client';
import { CompanyServices } from '@services/company-services';
import { stepsDictionary } from '@modules/wallet/wallet-sacar-modal/steps-dictionary';
import { useWalletPageStore } from '@stores';


const ERROR_CODE_PENDING_TERM = 'PENDING_TERMS'
const events = EVENTS_DICTIONARY[WalletPageName];

const getDefaultSacarOptions = () => ({
  value: undefined,
  bankAccount: {
    id: undefined,
    bankName: undefined,
    bankCode: undefined,
    agencia: undefined,
    agenciaDV: undefined,
    conta: undefined,
    contaDV: undefined,
    documentNumber: undefined,
    legalName: undefined,
  },
  password: undefined,
  tipoConta: undefined,
});

export const useWalletSacarModalStore = defineStore('WalletSacarModalStore', {
  state: () => ({
    isSacarModalOpen: false,
    showPendingTermsErrorModal: false,
    isConfirmTransactionModalOpen: false,
    saqueData: getDefaultSacarOptions(),
    currentStepName: 'SacarForm',
    transaction: null,
    modalSize: 'medium',
    transferToAnotherAccount: false,
    showPassword: false,
    banks: [],
    isSacarButtonLoading: false,
  }),
  getters: {
    currentStepComponent(state) {
      return stepsDictionary[state.currentStepName];
    },
  },
  actions: {
    async open() {
      this.isSacarButtonLoading = true;
      try {
        await Promise.all([this.requestBankAccount(), this.requestBanks()]);
      } finally {
        this.isSacarButtonLoading = false;
      }
      this.isSacarModalOpen = true;
      EventLogger.logEvent(events.openWithDrawModal);
    },
    close() {
      EventLogger.logEvent(events.closeWithDrawModal);
      this.$reset();
    },
    openSacarConfirmationModal() {
      this.isConfirmTransactionModalOpen = true;
    },
    closeSacarConfirmationModal() {
      this.saqueData.password = undefined;
      this.isConfirmTransactionModalOpen = false;
    },
    handleTransferToAnotherAccount() {
      EventLogger.logEvent(events.clickOnTranferToAnotherAccount);

      this.saqueData.bankAccount = {};
      this.transferToAnotherAccount = true;
    },
    async requestBankAccount() {
      const $interfaceState = useInterface();

      return CompanyServices.getBankAccount()
        .then(({ data }) => {
          if (notEmpty(data)) {
            this.saqueData.bankAccount.id = data.id;
            this.saqueData.bankAccount.bankName = data.bank_name;
            this.saqueData.bankAccount.bankCode = data.bank_code;
            this.saqueData.bankAccount.agencia = data.agencia;
            this.saqueData.bankAccount.agenciaDV = data.agencia_dv;
            this.saqueData.bankAccount.conta = data.conta;
            this.saqueData.bankAccount.contaDV = data.conta_dv;
            this.saqueData.bankAccount.documentNumber = data.document_number;
            this.saqueData.bankAccount.legalName = data.legal_name;
          }
          const $WalletPageStore = useWalletPageStore();
          this.saqueData.tipoConta = $WalletPageStore.currentActiveWalletName;
        })
        .catch((error) => {
          $interfaceState.toast.add({
            label: error?.error || 'Erro ao buscar os dados bancários',
            color: 'danger',
          });
        });
    },
    async requestBanks() {
      const $interfaceState = useInterface();

      return CompanyAPI.listBanks()
        .then((banks) => {
          this.banks = banks;
        })
        .catch(() => {
          $interfaceState.toast.add({
            label: 'Não foi possível carregar a lista de bancos. Tente novamente mais tarde.',
            color: 'danger',
          });
        });
    },
    async requestSaque() {
      const $WalletPageStore = useWalletPageStore();
      const $interfaceState = useInterface();

      return CompanyServices.saque(this.saqueData)
        .then((result) => {
          $WalletPageStore.walletsData[$WalletPageStore.currentActiveWalletName].saldo -= this.value;

          this.transaction = result;
          this.modalSize = 'small';
          this.currentStepName = 'SuccessSacar';

          EventLogger.logEvent(events.withDrawSuccessfull);
        })
        .catch((rejection) => {
          const defaultError = 'Saque indisponível no momento. Tente novamente mais tarde';
          const errorCode = rejection?.error_code;

          if (errorCode != ERROR_CODE_PENDING_TERM) {
              $interfaceState.toast.add({
                label: rejection?.error || defaultError,
                color: 'danger',
              });
          }
          this.showPendingTermsErrorModal = errorCode == ERROR_CODE_PENDING_TERM


          EventLogger.logEvent(events.withDrawUnsuccessful);
        })
        .finally(() => {
          this.saqueData.password = undefined;
        });
    },
  },
});
